import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { cx } from '@/lib/cx';
import styles from './Modal.module.css';

interface ModalProps {
  /** Names the dialog for a screen reader — it has no heading of its own to borrow. */
  label: string;
  onClose: () => void;
  /** How wide the surface may grow. `md` suits a form of short fields. */
  width?: 'md' | 'lg';
  children: ReactNode;
}

/**
 * A modal dialog, built on the native `<dialog>`.
 *
 * Native rather than a div with a high `z-index`, because `showModal()` is what puts
 * the element in the browser's **top layer** — and the top layer is where the focus
 * trap, the inert background, the Escape handling and the `::backdrop` come from. All
 * four are things a hand-rolled overlay has to re-earn, and the focus trap in
 * particular is the one everybody gets wrong.
 *
 * **Open state lives with the caller, not here.** This component is mounted when it
 * should be open and unmounted when it should not, so there is no `isOpen` prop and
 * nothing to keep in step. On both screens that state is a value in the URL.
 *
 * **A click on the backdrop does not close it.** That click lands on the dialog
 * element and is the usual way to dismiss one, but everything this wraps is a form:
 * a stray click beside it should not throw away what somebody has typed. Escape is a
 * deliberate keypress and Cancel is a button, which is enough ways out.
 */
export const Modal = ({ label, onClose, width = 'md', children }: ModalProps) => {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return undefined;

    // Not the `open` attribute, which shows the dialog inline and in normal flow
    // with none of the modal behaviour.
    if (!dialog.open) dialog.showModal();

    // Closed on the way out so the browser gives focus back and stops treating the
    // rest of the page as inert. React removing the node is not enough on its own.
    return () => {
      if (dialog.open) dialog.close();
    };
  }, []);

  return (
    <dialog
      ref={ref}
      className={cx(styles.dialog, styles[width])}
      aria-label={label}
      onCancel={(event) => {
        // Escape. The browser would close the ELEMENT while the caller — whose URL
        // decides whether this is mounted at all — went on thinking it was open, and
        // the next render would put a closed dialog back on screen. So the default is
        // prevented and the close is routed through the caller instead.
        event.preventDefault();
        onClose();
      }}
    >
      <div className={styles.surface}>{children}</div>
    </dialog>
  );
};
